import useAccountInformation from '@/auth/hooks/useAccountInformation'
import type {AccountInformation} from '@/auth/model'
import {getFoodGrubhubCart} from '@/lib/food-grubhub/foodGrubhubPaymentService'
import {useGetGrubhubUserAddress} from '@/lib/food-grubhub/store'
import type {SupportedLocales} from '@/lib/locale/locale'
import {NO_REFETCH_OPTIONS} from '@/utils/query/constants'

import useGrubhubGetAuth from './useGrubhubGetAuth'

export function getFoodGrubhubCartQueryKey(
  locale: SupportedLocales,
  account: AccountInformation | undefined,
) {
  return ['foodGrubhubCart', locale, account?.id] as const
}

export function getFoodGrubhubCartQueryOptions(
  locale: SupportedLocales,
  account: AccountInformation | undefined,
  getUserAddress: () => {
    longitude: number | null
    latitude: number | null
  },
  getAccessToken: () => string | null | undefined,
) {
  return queryOptions({
    queryKey: getFoodGrubhubCartQueryKey(locale, account),
    queryFn: async ({signal}) => {
      const isAuthenticated = !!account
      if (isAuthenticated) {
        return getFoodGrubhubCart({locale, isAuthenticated, signal})
      }

      const {longitude, latitude} = getUserAddress()
      const accessToken = getAccessToken()

      invariant(longitude && latitude, 'User address is required')
      invariant(accessToken, 'Access token is required')

      return getFoodGrubhubCart({
        locale,
        isAuthenticated: false,
        signal,
        foodToken: accessToken,
        longitude,
        latitude,
      })
    },
    throwOnError: false,
    ...NO_REFETCH_OPTIONS,
  })
}

export function useFoodGrubhubCart(locale: SupportedLocales) {
  const [account] = useAccountInformation()
  const store = useStore()
  const getUserAddress = useGetGrubhubUserAddress(store)

  const {data: authResponse} = useGrubhubGetAuth()
  const accessToken = authResponse?.session_handle.access_token
  const latestAccessToken = useLatest(accessToken)
  const getAccessToken = () => latestAccessToken.current
  return useQuery(getFoodGrubhubCartQueryOptions(locale, account, getUserAddress, getAccessToken))
}
